import React from 'react';
import { 
  FiPlay, 
  FiUsers, 
  FiStar, 
  FiTrendingUp,
  FiTarget,
  FiBookOpen,
  FiCheckCircle,
  FiInfo,
  FiSettings,
  FiBarChart2,
  FiTag
} from 'react-icons/fi';

const GamePreview = ({ game, onPlay }) => {
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}min`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'secondary';
      default: return 'secondary';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <div className="game-preview">
      {/* Header */}
      <div className="preview-header">
        <div className="game-title-section">
          <h2>{game.title}</h2>
          <div className="game-badges">
            <span className={`badge ${getStatusColor(game.status)}`}>
              {game.status}
            </span>
            <span className={`badge ${getDifficultyColor(game.difficulty)}`}>
              {game.difficulty}
            </span>
            {game.isFeatured && (
              <span className="badge featured">
                <FiStar /> Featured
              </span>
            )}
          </div>
        </div>
        
        <div className="game-stats-summary">
          <div className="stat">
            <FiPlay />
            <span>{game.analytics?.totalPlays || 0}</span>
            <small>Total Plays</small>
          </div>
          <div className="stat">
            <FiUsers />
            <span>{Math.round(game.analytics?.averageScore || 0)}%</span>
            <small>Avg Score</small>
          </div>
          <div className="stat">
            <FiTrendingUp />
            <span>{Math.round(game.analytics?.completionRate || 0)}%</span>
            <small>Completion</small>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="preview-section">
        <h3><FiInfo /> Description</h3>
        <p>{game.description}</p>
      </div>

      {/* Game Details */}
      <div className="preview-section">
        <h3><FiBookOpen /> Game Details</h3>
        <div className="details-grid">
          <div className="detail-item">
            <label>Subject</label>
            <span className="subject-badge">{game.subject}</span>
          </div>
          <div className="detail-item">
            <label>Game Type</label>
            <span>{game.gameType?.replace('-', ' ').toUpperCase()}</span>
          </div>
          <div className="detail-item">
            <label>Exam Types</label>
            <div className="exam-types">
              {game.examType?.map((exam, index) => (
                <span key={index} className="exam-badge">{exam}</span>
              )) || <span>None specified</span>}
            </div>
          </div>
          <div className="detail-item">
            <label>Age Range</label>
            <span>{game.metadata?.minAge || 13} - {game.metadata?.maxAge || 18} years</span>
          </div>
        </div>
      </div>

      {/* Topics */}
      {game.topics && game.topics.length > 0 && (
        <div className="preview-section">
          <h3><FiTarget /> Topics</h3>
          <div className="topics-list">
            {game.topics.map((topic, index) => (
              <span key={index} className="topic-tag">{topic}</span>
            ))}
          </div>
        </div>
      )}

      {/* Configuration */}
      <div className="preview-section">
        <h3><FiSettings /> Configuration</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>Time Limit</label>
            <span>{formatDuration(Math.floor((game.config?.timeLimit || 300) / 60))}</span>
          </div>
          <div className="config-item">
            <label>Max Attempts</label>
            <span>{game.config?.maxAttempts || 3}</span>
          </div>
          <div className="config-item">
            <label>Points per Correct</label>
            <span>{game.config?.pointsPerCorrect || 10}</span>
          </div>
          <div className="config-item">
            <label>Passing Score</label>
            <span>{game.config?.passingScore || 70}%</span>
          </div>
        </div>
        
        <div className="features">
          {game.config?.showHints && <span className="feature">Hints Available</span>}
          {game.config?.showExplanations && <span className="feature">Explanations</span>}
          {game.config?.randomizeQuestions && <span className="feature">Randomized</span>}
        </div>
      </div>

      {/* Questions Preview */}
      <div className="preview-section">
        <h3><FiCheckCircle /> Questions ({game.content?.questions?.length || 0})</h3>
        
        {game.content?.questions && game.content.questions.length > 0 ? (
          <div className="questions-preview">
            {game.content.questions.slice(0, 3).map((question, index) => (
              <div key={index} className="question-preview">
                <div className="question-header">
                  <span className="question-number">Q{index + 1}</span>
                  <span className="question-points">{question.points || 1} point{(question.points || 1) !== 1 ? 's' : ''}</span>
                </div>
                
                <p className="question-text">{question.text}</p>
                
                {question.type === 'multiple-choice' && question.options && (
                  <div className="options-preview">
                    {question.options.map((option, optionIndex) => (
                      <div 
                        key={optionIndex} 
                        className={`option ${question.correctAnswer === optionIndex ? 'correct' : ''}`}
                      >
                        <span className="option-label">{String.fromCharCode(65 + optionIndex)}.</span>
                        <span className="option-text">{option}</span>
                        {question.correctAnswer === optionIndex && <FiCheckCircle className="correct-icon" />}
                      </div>
                    ))}
                  </div>
                )}
                
                {question.explanation && (
                  <div className="explanation">
                    <strong>Explanation:</strong> {typeof question.explanation === 'string' ? question.explanation : question.explanation?.text || 'No explanation available'}
                  </div>
                )}
              </div>
            ))}
            
            {game.content.questions.length > 3 && (
              <div className="more-questions">
                ... and {game.content.questions.length - 3} more question{game.content.questions.length - 3 !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        ) : (
          <div className="no-questions">
            No questions added yet
          </div>
        )}
      </div>

      {/* Analytics */}
      {game.analytics && (
        <div className="preview-section">
          <h3><FiBarChart2 /> Analytics</h3>
          <div className="analytics-grid">
            <div className="analytics-item">
              <label>Total Plays</label>
              <span>{game.analytics.totalPlays || 0}</span>
            </div>
            <div className="analytics-item">
              <label>Average Score</label>
              <span>{Math.round(game.analytics.averageScore || 0)}%</span>
            </div>
            <div className="analytics-item">
              <label>Completion Rate</label>
              <span>{Math.round(game.analytics.completionRate || 0)}%</span>
            </div>
            <div className="analytics-item">
              <label>Average Duration</label>
              <span>{formatDuration(game.analytics.averageDuration || 0)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Learning Objectives */}
      {game.metadata?.learningObjectives && game.metadata.learningObjectives.length > 0 && (
        <div className="preview-section">
          <h3><FiTarget /> Learning Objectives</h3>
          <ul className="objectives-list">
            {game.metadata.learningObjectives.map((objective, index) => (
              <li key={index}>{objective}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Prerequisites */}
      {game.metadata?.prerequisites && game.metadata.prerequisites.length > 0 && (
        <div className="preview-section">
          <h3><FiBookOpen /> Prerequisites</h3>
          <ul className="prerequisites-list">
            {game.metadata.prerequisites.map((prerequisite, index) => (
              <li key={index}>{prerequisite}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tags */}
      {game.tags && game.tags.length > 0 && (
        <div className="preview-section">
          <h3><FiTag /> Tags</h3>
          <div className="tags-list">
            {game.tags.map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="preview-section">
        <h3><FiInfo /> Metadata</h3>
        <div className="metadata-grid">
          <div className="metadata-item">
            <label>Game ID</label>
            <span className="game-id">{game._id || 'New Game'}</span>
          </div>
          <div className="metadata-item">
            <label>Created</label>
            <span>{game.createdAt ? new Date(game.createdAt).toLocaleDateString() : 'Not saved'}</span>
          </div>
          <div className="metadata-item">
            <label>Last Updated</label>
            <span>{game.updatedAt ? new Date(game.updatedAt).toLocaleDateString() : 'Not saved'}</span>
          </div>
          <div className="metadata-item">
            <label>Estimated Duration</label>
            <span>{formatDuration(game.metadata?.estimatedDuration || 15)}</span>
          </div>
        </div>
      </div>

      {/* Play Action */}
      {onPlay && (
        <div className="preview-actions">
          <button 
            className="btn btn-primary btn-large"
            onClick={() => onPlay(game)}
          >
            <FiPlay /> Start Playing
          </button>
        </div>
      )}
    </div>
  );
};

export default GamePreview;